import { useEffect, useRef, useState, useCallback } from 'react';

const useInactivityTimeout = (timeoutDuration = 60000, onTimeout, isActive = true) => {
  const [isInactive, setIsInactive] = useState(false);
  const timeoutRef = useRef(null);
  const warningTimeoutRef = useRef(null);

  const resetTimer = useCallback(() => {
    setIsInactive(false);
    
    // Clear existing timers
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
    }

    if (isActive) {
      // Set timer for inactivity
      timeoutRef.current = setTimeout(() => {
        setIsInactive(true);
        onTimeout();
      }, timeoutDuration);
    }
  }, [timeoutDuration, onTimeout, isActive]);

  const handleActivity = useCallback(() => {
    if (isActive) {
      resetTimer();
    }
  }, [resetTimer, isActive]);

  useEffect(() => {
    if (!isActive) {
      // Clear timers if not active
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (warningTimeoutRef.current) {
        clearTimeout(warningTimeoutRef.current);
      }
      setIsInactive(false);
      return;
    }

    // Activity events to monitor
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

    // Add event listeners
    events.forEach(event => {
      document.addEventListener(event, handleActivity, true);
    });

    // Initialize timer
    resetTimer();

    // Cleanup function
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleActivity, true);
      });
      // Clear any existing timers
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (warningTimeoutRef.current) {
        clearTimeout(warningTimeoutRef.current);
        warningTimeoutRef.current = null;
      }
    };
  }, [handleActivity, resetTimer, isActive]);

  return {
    isInactive,
    resetTimer,
    clearTimers: () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (warningTimeoutRef.current) {
        clearTimeout(warningTimeoutRef.current);
      }
      setIsInactive(false);
    }
  };
};

export default useInactivityTimeout; 